import styled from "@emotion/styled";
import {
  DeleteOutline,
  DoNotDisturbOutlined,
  UndoOutlined
} from "@mui/icons-material";
import { Avatar, Box, Popover, Stack, Typography } from "@mui/material";
import RingsPhoto from "assets/images/Rings.png";
import { LoadingScreen } from "components/LoadingScreen";
import { Button, DeleteModal, Notification } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import i18next from "i18next";
import { isEmpty } from "lodash";
import { Fragment, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCentralStore } from "stores";
import { getEthiopianDate } from "utils/getEthiopianDate";
import { getGregorianDate } from "utils/getGregorianDate";
import "yet-another-react-lightbox/styles.css";
import {
  exportProfile,
  printProfile,
  useDeleteMember,
  useMember,
  useMergeChildren,
  usePotentialSpouses,
  useTerminateMember,
  useUpdateMember
} from "../api";
import {
  BasicProfileInformation,
  MemberJournal,
  SpouseMatchModal,
  TerminateMemberModal
} from "../components";
import { useMembersStore } from "../stores";
import {
  EMPTY_VALUE,
  formatMaritalBoolean,
  formatMaritalValue,
  isSingle
} from "../utils/maritalFields";
import { uniqueById } from "../utils/memberDataManipulator";

const Container = styled.div`
  padding: 22px 24px;

  ${({ theme }) => `${theme.breakpoints.down("sm")} {
    padding: 16px 12px;
  }`}
`;

const Tabs = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 22px;
`;

const Tab = styled.li`
  color: ${({ active, theme }) =>
    active ? theme.palette.primary.main : "inherit"};
  font-weight: ${({ active }) => (active ? 700 : 400)};
  border-radius: 5px;
  margin-bottom: 9px;
  cursor: pointer;
`;

const Section = styled.div`
  padding: 20px;
  border-radius: 8px;
  background: white;
  /* border: 0.5px solid gray; */
`;

const FamilySection = styled(Section)`
  display: flex;
  padding: 0;

  ${({ theme }) => `${theme.breakpoints.down("md")} {
    flex-direction: column;
  }`}
`;

const TeamSection = styled(Section)`
  display: flex;
  align-items: center;

  ${({ theme }) => `${theme.breakpoints.down("md")} {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }`}
`;

const Pill = styled.div`
  background: ${({ color }) => (color ? color : "#fbd8b0")};
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 20px;
  cursor: pointer;
  font-weight: 500;
`;

const DetailSection = styled(Section)`
  display: flex;

  ${({ theme }) => `${theme.breakpoints.down("md")} {
    flex-direction: column;
  }`}
`;

// Safely coerce a value that is expected to be text into something React can
// render. Some member records store fields like `languages`/`social_media_links`
// as objects instead of strings; rendering an object directly throws "Objects
// are not valid as a React child" and crashes the whole profile page.
const toDisplayText = value => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "object") {
    const candidate =
      value.name ?? value.language ?? value.value ?? value.url ?? value.link;
    return candidate == null ? "" : String(candidate);
  }
  return "";
};

// Lightweight fallback for a single profile sub-section. Keeps the rest of the
// profile usable when one section's data is malformed instead of letting the
// route-level error boundary replace the entire page.
const SectionErrorFallback = () => (
  <Stack spacing={1} sx={{ py: 4 }} alignItems="center">
    <Typography variant="subtitle2" color="text.secondary" textAlign="center">
      This section couldn&apos;t be displayed.
    </Typography>
  </Stack>
);

export const MemberProfileV2 = () => {
  const tabs = ["members.profile.MemberProfile", "Member Journal"];
  const [activeTab, setActiveTab] = useState("members.profile.MemberProfile");
  const [activeDetailsTab, setActiveDetailsTab] = useState("Profile");
  const navigations = [
    { name: "Profile", translation: "addMembers.step1.PersonalProfile" },
    { name: "Address", translation: "addMembers.step1.Address" },
    {
      name: "Emergency Contact",
      translation: "addMembers.step1.EmergencyContact"
    },
    {
      name: "Academic Status",
      translation: "addMembers.step2.AcademicStatus"
    },
    {
      name: "Job Status",
      translation: "addMembers.step2.JobStatus"
    },
    { name: "Marital Status", translation: "addMembers.step3.MaritalStatus" },
    { name: "Child Info", translation: "addMembers.step3.ChildInfo" },
    {
      name: "Previous Church info",
      translation: "addMembers.step4.PreviousChurchInfo"
    },
    {
      name: "Current Church",
      translation: "addMembers.step5.CurrentChurchEngagementInfo"
    },
    {
      name: "Engagement Info",
      translation: "addMembers.step5.MinistryEngagementInformation"
    }
  ];
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const { t } = useTranslation();
  const setFullMemberData = useMembersStore(state => state.setFullMemberData);
  const openSpouseMatchedModal = useMembersStore(
    state => state.openSpouseMatchedModal
  );
  const setMatchedSpouseData = useMembersStore(
    state => state.setMatchedSpouseData
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const { id } = useParams();
  const setHeaderTitle = useCentralStore(state => state.setHeaderTitle);
  const setNotification = useCentralStore(state => state.setNotification);
  const memberDeleteMutation = useDeleteMember();
  const [datePreview, setDatePreview] = useState("amh");

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = useNavigate();
  const {
    data: memberData,
    isLoading,
    isError
  } = useMember({
    churchId: selectedChurch?.id,
    memberId: id
  });

  useEffect(() => {
    if (memberData) {
      setFullMemberData(memberData.result);
    }
  }, [memberData]);

  const { refetch: matchSpouse } = usePotentialSpouses({
    churchId: selectedChurch?.id,
    memberId: id,
    config: {
      enabled: false,
      onSuccess: response => {
        if (response.status) {
          if (response.result) {
            setMatchedSpouseData(response.result);
            openSpouseMatchedModal();
          } else {
            setNotification({
              type: "info",
              message: "Spouse not found"
            });
          }
        }
      }
    }
  });

  const mergeChildrenMutation = useMergeChildren();

  useEffect(() => {
    setHeaderTitle(t("members.profile.MemberProfile"));
    return () => setHeaderTitle("");
  }, [i18next.language]);

  const handleExport = () => {
    exportProfile({
      type: "member",
      churchId: selectedChurch?.id,
      id: id,
      format: "pdf",
      orientation: "landscape"
    });
  };

  const handlePrint = () => {
    printProfile({
      type: "member",
      churchId: selectedChurch?.id,
      id: id,
      format: "pdf",
      orientation: "landscape"
    });
  };

  const getSpouseName = () => {
    const parts = fullMemberData?.spouse
      ? [
          fullMemberData?.spouse?.first_name,
          fullMemberData?.spouse?.father_name,
          fullMemberData?.spouse?.last_name
        ]
      : [
          fullMemberData?.spouse_first_name,
          fullMemberData?.spouse_father_name,
          fullMemberData?.spouse_last_name
        ];

    return parts.filter(Boolean).join(" ");
  };

  const childrenList =
    fullMemberData?.spouse &&
    String(fullMemberData?.has_children_merged) === "0"
      ? [
          ...(fullMemberData?.children ?? []),
          ...(fullMemberData?.spouse?.children ?? [])
        ]
      : fullMemberData?.children;

  const canMergeChildren =
    String(fullMemberData?.marital_status) === "married" &&
    String(fullMemberData?.has_children_merged) === "0" &&
    Number(fullMemberData?.is_spouse_member) === 1 &&
    Number(fullMemberData?.is_spouse_believer) === 1 &&
    fullMemberData?.spouse &&
    (!isEmpty(fullMemberData?.children) ||
      !isEmpty(fullMemberData?.spouse?.children));

  if (isLoading) {
    return <LoadingScreen />;
  }

  // The member could not be loaded — invalid/missing id (e.g. a row that
  // arrived without an id) or a record that no longer exists. Fail loudly
  // instead of rendering a blank profile from stale store data.
  if (isError || !memberData?.result) {
    return (
      <Container>
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 8, minHeight: "100vh" }}
        >
          <Typography variant="h6">
            {t("members.profile.MemberNotFoundTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t("members.profile.MemberNotFoundMessage")}
          </Typography>
          <Button flavor="primary" onClick={() => navigate("/members")}>
            {t("form.common.back")}
          </Button>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <DeleteModal
        title={t("members.profile.DeleteMember")}
        onDelete={() => {
          memberDeleteMutation.mutate({
            churchId: selectedChurch?.id,
            memberId: fullMemberData?.id
          });
        }}
        name={`${fullMemberData?.first_name} ${fullMemberData?.father_name}`}
      />
      <Tabs>
        {tabs.map(tabItem => (
          <Tab
            key={tabItem}
            active={tabItem === activeTab}
            onClick={() => setActiveTab(tabItem)}
          >
            {t(tabItem)}
          </Tab>
        ))}
      </Tabs>
      {activeTab === tabs[0] && (
        <>
          <Stack spacing="13px">
            <BasicProfileInformation
              handleClick={handleClick}
              handleExport={handleExport}
              handlePrint={handlePrint}
            />

            <FamilySection>
              <Stack flex={1} spacing="20px" py="20px" px="24px">
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                  spacing="20px"
                >
                  {/* <Avatar
                  sx={{
                    width: "140px",
                    height: "140px",
                    border: "2px solid",
                    borderColor: "primary.main"
                  }}
                  src={`${IMAGE_URL}/${fullMemberData?.profile_image_url}`}
                  alt={fullMemberData?.first_name}
                /> */}
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center"
                      spacing={10}
                    >
                      <Stack>
                        <Typography variant="subtitle2">
                          {t("addMembers.step3.SpouseName")}
                        </Typography>
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          useFlexGap
                          alignItems="center"
                          spacing={2}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: "primary.main",
                              cursor: fullMemberData?.spouse
                                ? "pointer"
                                : "inherit"
                            }}
                            onClick={() => {
                              if (fullMemberData?.spouse) {
                                window.open(
                                  `/members/${fullMemberData?.spouse?.id}`,
                                  "__blank"
                                );
                              }
                            }}
                          >
                            {formatMaritalValue(
                              fullMemberData?.marital_status,
                              getSpouseName()
                            )}
                          </Typography>
                          {fullMemberData?.status === "active" &&
                            fullMemberData?.marital_status === "married" &&
                            fullMemberData?.is_spouse_of === null && (
                              <Button
                                flavor="secondary"
                                onClick={() => matchSpouse()}
                              >
                                Match
                              </Button>
                            )}
                        </Stack>
                      </Stack>
                      <Stack>
                        <Typography variant="subtitle2">
                          {t("members.profile.Children")}
                        </Typography>
                        {isEmpty(childrenList) && (
                          <Typography
                            sx={{ fontWeight: 700, color: "primary.main" }}
                          >
                            No children
                          </Typography>
                        )}
                        <Stack>
                          {(childrenList || []).map(child => (
                            <Typography
                              key={child.id}
                              sx={{ fontWeight: 700, color: "primary.main" }}
                            >
                              {child.first_name} {child.father_name}{" "}
                              {child.last_name}
                            </Typography>
                          ))}
                          {canMergeChildren && (
                            <Button
                              onClick={() =>
                                mergeChildrenMutation.mutate({
                                  churchId: selectedChurch?.id,
                                  memberId: id,
                                  spouseId: fullMemberData?.spouse?.id
                                })
                              }
                            >
                              Merge Children
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="center"
                      spacing={5}
                    >
                      <Stack>
                        <Typography variant="subtitle2">
                          {t("form.common.phone")}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: 700, color: "primary.main" }}
                        >
                          {formatMaritalValue(
                            fullMemberData?.marital_status,
                            fullMemberData?.spouse_phone_number
                          )}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
              {fullMemberData?.marital_status !== "single" && (
                <Stack
                  sx={{
                    background: `linear-gradient(257.23deg, #FBD8B0 100%, #FBD8B0 100%), url(${RingsPhoto})`,
                    backgroundBlendMode: "multiply",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    py: "40px",
                    px: "16px",
                    color: "white",
                    width: { xs: "100%", md: "327px" },
                    alignItems: "stretch"
                  }}
                  spacing={1}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    <Trans i18nKey="members.profile.MaritalDate" />
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {fullMemberData?.marital_date ? (
                      <Typography>
                        {datePreview === "eng"
                          ? getGregorianDate(fullMemberData?.marital_date)
                          : getEthiopianDate(fullMemberData?.marital_date)}
                      </Typography>
                    ) : null}
                    {fullMemberData?.marital_date && (
                      <Typography
                        onClick={() => {
                          if (datePreview === "amh") setDatePreview("eng");
                          if (datePreview === "eng") setDatePreview("amh");
                        }}
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: "bold",
                          cursor: "pointer",
                          color: "white",
                          userSelect: "none"
                        }}
                      >
                        {datePreview}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              )}
            </FamilySection>

            <TeamSection>
              <Stack flex={1} sx={{ borderRight: "0.5px solid #9CA3AF" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t("members.profile.TeamEngagement")}
                </Typography>
                <Typography variant="caption">
                  {t("members.profile.MemberTeamEngagement")}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing="18px"
                  mt="18px"
                >
                  {uniqueById(fullMemberData?.teams).map(team => (
                    <Pill key={team.id}>{team.name}</Pill>
                  ))}
                </Stack>
              </Stack>
              <Stack flex={1} sx={{ marginLeft: { xs: 0, md: "38px" } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t("members.profile.ChurchContribution")}
                </Typography>
                <Typography variant="caption">
                  {t("members.profile.TotalChurchContribution")}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing="18px"
                  mt="18px"
                >
                  {/* <Pill color="#74DFD5">5790 Birr</Pill> */}
                </Stack>
              </Stack>
            </TeamSection>

            <DetailSection>
              <Stack
                pr={{ md: 1 }}
                pb={{ xs: 1, md: 0 }}
                sx={{
                  borderRight: { md: "0.5px solid #9CA3AF" },
                  borderBottom: {
                    xs: "0.5px solid #9CA3AF",
                    md: "none"
                  }
                }}
              >
                {navigations.map(navigation => (
                  <Typography
                    key={navigation.name}
                    variant="subtitle2"
                    p="10px"
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: `${
                        activeDetailsTab === navigation.name ? 700 : 400
                      }`,
                      background: `${
                        activeDetailsTab === navigation.name
                          ? "#F3F4F6"
                          : "white"
                      }`,
                      cursor: "pointer",
                      borderRadius: "4px",
                      ":hover": {
                        background: "#F3F4F6"
                      }
                    }}
                    onClick={() => setActiveDetailsTab(navigation.name)}
                  >
                    {t(navigation.translation)}
                  </Typography>
                ))}
              </Stack>
              <Box
                pl={{ xs: 0, md: 4 }}
                pt={{ xs: 2, md: 0 }}
                flex={1}
                sx={{ textTransform: "capitalize", minWidth: 0 }}
              >
                <ErrorBoundary
                  FallbackComponent={SectionErrorFallback}
                  resetKeys={[activeDetailsTab]}
                >
                  {navigationLookup[activeDetailsTab]}
                </ErrorBoundary>
              </Box>
            </DetailSection>
          </Stack>
          <Popover
            anchorEl={anchorEl}
            id="profile-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 2
            }}
          >
            <ProfileMenu />
          </Popover>
        </>
      )}
      {activeTab === tabs[1] && (
        <MemberJournal
          handleClick={handleClick}
          handleExport={handleExport}
          handlePrint={handlePrint}
        />
      )}
      <TerminateMemberModal />
      <Notification />
      <SpouseMatchModal />
    </Container>
  );
};

const PersonalProfile = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const [datePreview, setDatePreview] = useState("amh");

  return (
    <Stack alignItems="flex-start" direction="row" flexWrap="wrap" useFlexGap>
      <Stack spacing={4} flex={1}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.MotherName")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.mother_first_name}{" "}
            {fullMemberData?.mother_father_name}{" "}
            {fullMemberData?.mother_grand_father_name}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.Nationality")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.nationality?.nationality}
          </Typography>
        </Stack>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>
              {t("addMembers.step1.IdType")}
            </Typography>
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {fullMemberData?.id_type}
            </Typography>
          </Stack>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>
              {t("addMembers.step1.IdNumber")}
            </Typography>
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {fullMemberData?.id_number}
            </Typography>
          </Stack>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.Birthplace")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("form.common.country")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.birth_country?.name}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("form.common.region")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.birth_region?.name}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("form.common.city")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.birth_city?.name}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("form.common.dateOfBirth")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {fullMemberData?.date_of_birth ? (
                  <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                    {datePreview === "eng"
                      ? getGregorianDate(fullMemberData?.date_of_birth)
                      : getEthiopianDate(fullMemberData?.date_of_birth)}
                  </Typography>
                ) : null}
                {fullMemberData?.date_of_birth ? (
                  <Typography
                    onClick={() => {
                      if (datePreview === "amh") setDatePreview("eng");
                      if (datePreview === "eng") setDatePreview("amh");
                    }}
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      cursor: "pointer",
                      color: "primary.main",
                      userSelect: "none"
                    }}
                  >
                    {datePreview}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.Language")}
          </Typography>
          <Stack>
            {fullMemberData?.languages?.map((language, index) => (
              <Typography
                key={index}
                sx={{ color: "primary.main", fontWeight: 700 }}
              >
                {toDisplayText(language)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="subtitle2">
          {t("members.profile.IDCard")}
        </Typography>
        <Avatar
          sx={{
            width: "280px",
            maxWidth: "100%",
            height: "154px",
            borderRadius: "8px"
          }}
          imgProps={{
            style: {
              objectFit: "contain"
            }
          }}
          src={`${IMAGE_URL}/${fullMemberData?.id_image_url}`}
          alt="Id card"
          variant="square"
        />
      </Stack>
    </Stack>
  );
};

const Address = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.PhoneNumber")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.phone}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.OfficeNumber")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.office_number}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.email")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.email}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.POBox")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.po_box}
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step1.SocialMediaLink")}
        </Typography>
        {fullMemberData?.social_media_links?.map((socialLink, index) => (
          <Typography
            key={index}
            sx={{
              color: "primary.main",
              fontWeight: 700,
              textTransform: "lowercase"
            }}
          >
            {toDisplayText(socialLink)}
          </Typography>
        ))}
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step1.Nationality")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {fullMemberData?.nationality?.nationality}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.country")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_country?.name}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.region")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_region?.name}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.zone")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_zone?.name}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.city")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_city?.name}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.subcity")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_sub_city?.name}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.woreda")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_woreda?.name}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.kebele")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_kebele}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.houseNumber")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.house_number}
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step1.SpecificLocation")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {fullMemberData?.specific_location}
        </Typography>
      </Stack>
    </Stack>
  );
};

const EmergencyContact = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("form.common.fullName")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {fullMemberData?.emergency_contact_first_name}{" "}
          {fullMemberData?.emergency_contact_father_name}{" "}
          {fullMemberData?.emergency_contact_last_name}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.phone")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.emergency_contact_phone}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step1.Relationship")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.emergency_contact_relationship}
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("form.common.address")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>
              {t("form.common.country")}
            </Typography>
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {fullMemberData?.emergency_contact_country?.name}
            </Typography>
          </Stack>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>
              {t("form.common.region")}
            </Typography>
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {fullMemberData?.emergency_contact_region?.name}
            </Typography>
          </Stack>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>
              {t("form.common.city")}
            </Typography>
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {fullMemberData?.emergency_contact_city?.name}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

const AcademicStatus = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step2.AcademicStatus")}
        </Typography>
        {fullMemberData?.education_and_skills?.map(
          (education_and_skill, index) => (
            <Typography
              key={index}
              sx={{ color: "primary.main", fontWeight: 700 }}
            >
              {education_and_skill.academic_status}:{" "}
              {education_and_skill.professional_skill}
            </Typography>
          )
        )}
      </Stack>
    </Stack>
  );
};

const JobStatus = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step2.FullTimeJob")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {fullMemberData?.job_status}
        </Typography>
      </Stack>
      {fullMemberData?.job_status !== "student" && (
        <>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step2.Profession")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.profession}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step2.Workplace")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.work_place}
              </Typography>
            </Stack>
          </Stack>
        </>
      )}
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step2.PartTimeJob")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {fullMemberData?.custom_job_status}
        </Typography>
      </Stack>
    </Stack>
  );
};

const MaritalStatus = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const [datePreview, setDatePreview] = useState("amh");

  const getSpouseName = () => {
    const parts = fullMemberData?.spouse
      ? [
          fullMemberData?.spouse?.first_name,
          fullMemberData?.spouse?.father_name,
          fullMemberData?.spouse?.last_name
        ]
      : [
          fullMemberData?.spouse_first_name,
          fullMemberData?.spouse_father_name,
          fullMemberData?.spouse_last_name
        ];

    return parts.filter(Boolean).join(" ");
  };

  const maritalStatus = fullMemberData?.marital_status;
  const single = isSingle(maritalStatus);
  const showDate = field => !single && fullMemberData?.[field];

  const spouseChurchName =
    Number(fullMemberData?.is_spouse_member) === 1
      ? selectedChurch?.name
      : fullMemberData?.spouse_church_name;

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.MaritalStatus")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {maritalStatus || EMPTY_VALUE}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.MaritalDate")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {showDate("marital_date") ? (
              <>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {datePreview === "eng"
                    ? getGregorianDate(fullMemberData?.marital_date)
                    : getEthiopianDate(fullMemberData?.marital_date)}
                </Typography>
                <Typography
                  onClick={() => {
                    if (datePreview === "amh") setDatePreview("eng");
                    if (datePreview === "eng") setDatePreview("amh");
                  }}
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: "primary.main",
                    userSelect: "none"
                  }}
                >
                  {datePreview}
                </Typography>
              </>
            ) : (
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {EMPTY_VALUE}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.TypeOfMarriage")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {formatMaritalValue(
              maritalStatus,
              fullMemberData?.type_of_marriage
            )}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.IsSpouseABeliever")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {formatMaritalBoolean(
              maritalStatus,
              fullMemberData?.is_spouse_believer
            )}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.IsSpouseAMember")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {formatMaritalBoolean(
              maritalStatus,
              fullMemberData?.is_spouse_member
            )}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.fullName")}
          </Typography>
          <Typography
            sx={{
              color: "primary.main",
              fontWeight: 700
            }}
          >
            {formatMaritalValue(maritalStatus, getSpouseName())}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.phone")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {formatMaritalValue(
              maritalStatus,
              fullMemberData?.spouse_phone_number
            )}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("form.common.dateOfBirth")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {showDate("spouse_date_of_birth") ? (
              <>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {datePreview === "eng"
                    ? getGregorianDate(fullMemberData?.spouse_date_of_birth)
                    : getEthiopianDate(fullMemberData?.spouse_date_of_birth)}
                </Typography>
                <Typography
                  onClick={() => {
                    if (datePreview === "amh") setDatePreview("eng");
                    if (datePreview === "eng") setDatePreview("amh");
                  }}
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: "primary.main",
                    userSelect: "none"
                  }}
                >
                  {datePreview}
                </Typography>
              </>
            ) : (
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {EMPTY_VALUE}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step3.SpouseMotherName")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {formatMaritalValue(
            maritalStatus,
            [
              fullMemberData?.spouse_mother_name,
              fullMemberData?.spouse_mother_father_name,
              fullMemberData?.spouse_mother_grand_father_name
            ]
              .filter(Boolean)
              .join(" ")
          )}
        </Typography>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.SpouseProfessionalSkills")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {formatMaritalValue(
              maritalStatus,
              fullMemberData?.spouse_professional_skill
            )}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step3.SpouseOfficeName")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {formatMaritalValue(
              maritalStatus,
              fullMemberData?.spouse_office_name
            )}
          </Typography>
        </Stack>
      </Stack>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step3.SpouseChurchName")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {formatMaritalValue(maritalStatus, spouseChurchName)}
        </Typography>
      </Stack>
    </Stack>
  );
};

const ChildInfo = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const [datePreview, setDatePreview] = useState("amh");

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step3.HaveChildrenOrDependencies")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {Number(fullMemberData?.child_and_dependencies) ? "Yes" : "No"}
        </Typography>
      </Stack>
      {!isEmpty(fullMemberData?.children) &&
        fullMemberData?.children?.map(child => (
          <Fragment key={child.id}>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
              <Stack>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("form.common.fullName")}
                </Typography>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {child?.first_name} {child?.father_name} {child?.last_name}
                </Typography>
              </Stack>
              <Stack>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("form.common.gender")}
                </Typography>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {child?.gender}
                </Typography>
              </Stack>
              <Stack>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("addMembers.step3.Nationality")}
                </Typography>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {child?.nationality?.nationality}
                </Typography>
              </Stack>
              <Stack>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("addMembers.step3.RelationshipWithChild")}
                </Typography>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {child?.member_children?.[0]?.relation_type}
                </Typography>
              </Stack>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step3.PlaceOfBirth")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
                <Stack>
                  <Typography sx={{ fontWeight: 500 }}>
                    {t("form.common.country")}
                  </Typography>
                  <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                    {child?.birth_country?.name}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography sx={{ fontWeight: 500 }}>
                    {t("form.common.region")}
                  </Typography>
                  <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                    {child?.birth_region?.name}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography sx={{ fontWeight: 500 }}>
                    {t("form.common.city")}
                  </Typography>
                  <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                    {child?.birth_city?.name}
                  </Typography>
                </Stack>
                <Stack>
                  <Typography sx={{ fontWeight: 500 }}>
                    {t("form.common.dateOfBirth")}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                    {child?.date_of_birth ? (
                      <Typography
                        sx={{ color: "primary.main", fontWeight: 700 }}
                      >
                        {datePreview === "eng"
                          ? getGregorianDate(child?.date_of_birth)
                          : getEthiopianDate(child?.date_of_birth)}
                      </Typography>
                    ) : null}
                    {child?.date_of_birth ? (
                      <Typography
                        onClick={() => {
                          if (datePreview === "amh") setDatePreview("eng");
                          if (datePreview === "eng") setDatePreview("amh");
                        }}
                        sx={{
                          textTransform: "uppercase",
                          fontWeight: "bold",
                          cursor: "pointer",
                          color: "primary.main",
                          userSelect: "none"
                        }}
                      >
                        {datePreview}
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step1.MotherName")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {child?.mother_name} {child?.mother_father_name}{" "}
                {child?.mother_last_name}
              </Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
              <Stack>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("addMembers.step3.IsChildBeliever")}
                </Typography>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {Number(child?.is_believer) ? "Yes" : "No"}
                </Typography>
              </Stack>
              <Stack>
                <Typography sx={{ fontWeight: 500 }}>
                  {t("addMembers.step3.IsChildAMember")}
                </Typography>
                <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                  {Number(child?.is_member) ? "Yes" : "No"}
                </Typography>
              </Stack>
            </Stack>
          </Fragment>
        ))}
    </Stack>
  );
};

const PreviousChurchInfo = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const [datePreview, setDatePreview] = useState("amh");

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step4.AreYouTransferredFromOtherChurch")}
        </Typography>
        <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
          {Number(fullMemberData?.transfer_from_other_church) ? "Yes" : "No"}
        </Typography>
      </Stack>
      {fullMemberData?.transfer_from_other_church === 1 && (
        <>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step4.FormerChurchName")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.previous_church_name}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step4.DateOfResignation")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {fullMemberData?.date_of_resignation ? (
                  <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                    {datePreview === "eng"
                      ? getGregorianDate(fullMemberData?.date_of_resignation)
                      : getEthiopianDate(fullMemberData?.date_of_resignation)}
                  </Typography>
                ) : null}
                {fullMemberData?.date_of_resignation ? (
                  <Typography
                    onClick={() => {
                      if (datePreview === "amh") setDatePreview("eng");
                      if (datePreview === "eng") setDatePreview("amh");
                    }}
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      cursor: "pointer",
                      color: "primary.main",
                      userSelect: "none"
                    }}
                  >
                    {datePreview}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step4.PreviousChurchPastorInformation")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.pastor_full_name}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("form.common.phone")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.pastor_phone_number}
              </Typography>
            </Stack>
          </Stack>
          <Stack>
            <Typography sx={{ fontWeight: 500 }}>
              {t("addMembers.step4.ReasonForResignation")}
            </Typography>
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {fullMemberData?.reason_for_resignation}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <Typography sx={{ fontWeight: 500 }}>
              {t("addMembers.step4.LetterOfResignation")}
            </Typography>
            {isEmpty(fullMemberData?.letter_of_resignation) ? (
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                No letter uploaded
              </Typography>
            ) : (
              <Button
                variant="contained"
                onClick={() => {
                  window.open(
                    `${IMAGE_URL}/${fullMemberData?.letter_of_resignation}`,
                    "_blank"
                  );
                }}
              >
                View Letter
              </Button>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
};

const SalvationProfile = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const [datePreview, setDatePreview] = useState("amh");

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.FormerBelief")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.former_belief}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.HowDidYouAcceptJesusChrist")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.how_you_accept_christ}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.SalvationDate")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {fullMemberData?.salvation_date ? (
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {datePreview === "eng"
                  ? getGregorianDate(fullMemberData?.salvation_date)
                  : getEthiopianDate(fullMemberData?.salvation_date)}
              </Typography>
            ) : null}
            {fullMemberData?.salvation_date && (
              <Typography
                onClick={() => {
                  if (datePreview === "amh") setDatePreview("eng");
                  if (datePreview === "eng") setDatePreview("amh");
                }}
                sx={{
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  cursor: "pointer",
                  color: "primary.main",
                  userSelect: "none"
                }}
              >
                {datePreview}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.AreYouBaptized")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {Number(fullMemberData?.is_baptized) ? "Yes" : "No"}
          </Typography>
        </Stack>
        {fullMemberData?.is_baptized === 1 && (
          <>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step5.BaptizedChurch")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.baptism_church}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step5.DateOfBaptism")}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {fullMemberData?.baptism_date ? (
                  <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                    {datePreview === "eng"
                      ? getGregorianDate(fullMemberData?.baptism_date)
                      : getEthiopianDate(fullMemberData?.baptism_date)}
                  </Typography>
                ) : null}
                {fullMemberData?.baptism_date && (
                  <Typography
                    onClick={() => {
                      if (datePreview === "amh") setDatePreview("eng");
                      if (datePreview === "eng") setDatePreview("amh");
                    }}
                    sx={{
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      cursor: "pointer",
                      color: "primary.main",
                      userSelect: "none"
                    }}
                  >
                    {datePreview}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.IsThereHomeCellInYourChurch")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {Number(fullMemberData?.is_home_cell) ? "Yes" : "No"}
          </Typography>
        </Stack>
        {fullMemberData?.is_home_cell === 1 && (
          <>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step5.HomeCellYouEngagedIn")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.home_cell_name}
              </Typography>
            </Stack>
            <Stack>
              <Typography sx={{ fontWeight: 500 }}>
                {t("addMembers.step5.HomeCellCoordinator")}
              </Typography>
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {fullMemberData?.home_cell_cordinator}
              </Typography>
            </Stack>
          </>
        )}
      </Stack>
      {fullMemberData?.is_home_cell === 1 && (
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.DateYouStartEngaging")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {fullMemberData?.date_joined_home_cell ? (
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {datePreview === "eng"
                  ? getGregorianDate(fullMemberData?.date_joined_home_cell)
                  : getEthiopianDate(fullMemberData?.date_joined_home_cell)}
              </Typography>
            ) : null}
            {fullMemberData?.date_joined_home_cell && (
              <Typography
                onClick={() => {
                  if (datePreview === "amh") setDatePreview("eng");
                  if (datePreview === "eng") setDatePreview("amh");
                }}
                sx={{
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  cursor: "pointer",
                  color: "primary.main",
                  userSelect: "none"
                }}
              >
                {datePreview}
              </Typography>
            )}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const CurrentChurchEngagementInfo = () => {
  const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const [datePreview, setDatePreview] = useState("amh");

  return (
    <Stack alignItems="flex-start" spacing={4}>
      <Stack>
        <Typography sx={{ fontWeight: 500 }}>
          {t("addMembers.step5.MembershipDate")}
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
          {fullMemberData?.membership_date ? (
            <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
              {datePreview === "eng"
                ? getGregorianDate(fullMemberData?.membership_date)
                : getEthiopianDate(fullMemberData?.membership_date)}
            </Typography>
          ) : null}
          {fullMemberData?.membership_date && (
            <Typography
              onClick={() => {
                if (datePreview === "amh") setDatePreview("eng");
                if (datePreview === "eng") setDatePreview("amh");
              }}
              sx={{
                textTransform: "uppercase",
                fontWeight: "bold",
                cursor: "pointer",
                color: "primary.main",
                userSelect: "none"
              }}
            >
              {datePreview}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.YourCurrentMinistry")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.current_ministry}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.TheDateYouStartEngaging")}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {fullMemberData?.date_joined_current_ministry ? (
              <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
                {datePreview === "eng"
                  ? getGregorianDate(
                      fullMemberData?.date_joined_current_ministry
                    )
                  : getEthiopianDate(
                      fullMemberData?.date_joined_current_ministry
                    )}
              </Typography>
            ) : null}
            {fullMemberData?.date_joined_current_ministry && (
              <Typography
                onClick={() => {
                  if (datePreview === "amh") setDatePreview("eng");
                  if (datePreview === "eng") setDatePreview("amh");
                }}
                sx={{
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  cursor: "pointer",
                  color: "primary.main",
                  userSelect: "none"
                }}
              >
                {datePreview}
              </Typography>
            )}
          </Stack>
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.YourRoleInTheMinistry")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.role_in_current_ministry}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4}>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.MinistryPreferences")}
          </Typography>
          {fullMemberData?.ministry_preference?.map((preference, index) => (
            <Typography
              key={index}
              sx={{ color: "primary.main", fontWeight: 700 }}
            >
              {toDisplayText(preference?.ministry)}
            </Typography>
          ))}
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: 500 }}>
            {t("addMembers.step5.GiftsAndTalents")}
          </Typography>
          <Typography sx={{ color: "primary.main", fontWeight: 700 }}>
            {fullMemberData?.gifts_and_talents}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const navigationLookup = {
  Profile: <PersonalProfile />,
  Address: <Address />,
  "Emergency Contact": <EmergencyContact />,
  "Academic Status": <AcademicStatus />,
  "Job Status": <JobStatus />,
  "Marital Status": <MaritalStatus />,
  "Child Info": <ChildInfo />,
  "Previous Church info": <PreviousChurchInfo />,
  "Current Church": <SalvationProfile />,
  "Engagement Info": <CurrentChurchEngagementInfo />
};

const ProfileMenu = () => {
  // const { t } = useTranslation();
  const fullMemberData = useMembersStore(state => state.fullMemberData);
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const terminateMutation = useTerminateMember();
  const updateMemberMutation = useUpdateMember();
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);
  const { t } = useTranslation();

  return (
    <Box sx={{ minWidth: "168px", overflow: "hidden", borderRadius: "5px" }}>
      <Box
        sx={{
          p: 2,
          backgroundColor: "primary.main",
          color: "primary.contrastText"
        }}
      >
        {t("members.profile.ProfileSetting")}
      </Box>
      <Stack
        sx={{
          backgroundColor: "white",
          py: "9px",
          px: "12px"
        }}
        spacing={1}
      >
        {fullMemberData?.status === "active" && (
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={2}
            alignItems="center"
            sx={{
              color: "primary.main",
              backgroundColor: "secondary.main",
              py: 1,
              px: 2,
              cursor: "pointer"
            }}
            onClick={() =>
              terminateMutation.mutate({
                churchId: selectedChurch?.id,
                memberId: fullMemberData?.id
              })
            }
          >
            <DoNotDisturbOutlined />
            <Typography variant="caption">
              {t("members.profile.TerminateMember")}
            </Typography>
          </Stack>
        )}
        {fullMemberData?.status === "inactive" && (
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={2}
            alignItems="center"
            sx={{
              color: "primary.main",
              backgroundColor: "secondary.main",
              py: 1,
              px: 2,
              cursor: "pointer"
            }}
            onClick={() =>
              updateMemberMutation.mutate({
                churchId: selectedChurch?.id,
                memberId: fullMemberData?.id,
                memberData: {
                  status: "active"
                }
              })
            }
          >
            <UndoOutlined />
            <Typography variant="caption">Reinstate member</Typography>
          </Stack>
        )}
        {fullMemberData?.status === "reject" && (
          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={2}
            alignItems="center"
            sx={{
              color: "primary.main",
              backgroundColor: "secondary.main",
              py: 1,
              px: 2,
              cursor: "pointer"
            }}
            onClick={() =>
              updateMemberMutation.mutate({
                churchId: selectedChurch?.id,
                memberId: fullMemberData?.id,
                memberData: {
                  status: "pending"
                }
              })
            }
          >
            <UndoOutlined />
            <Typography variant="caption">Reinstate member</Typography>
          </Stack>
        )}
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={2}
          alignItems="center"
          sx={{
            backgroundColor: "#FCD1D1",
            color: "#F44949",
            py: 1,
            px: 2,
            cursor: "pointer"
          }}
          onClick={openDeleteModal}
        >
          <DeleteOutline />
          <Typography variant="caption">
            {t("members.profile.DeleteMember")}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
